import { MirrorData, SetMirrorData } from '@type/job-add';
import { SetFormValues } from '@type/form-types';
import { useEffect, useMemo, useState } from 'react';
import { useSwrJobDomain } from '@hooks/useSwrApi/endpoints/domain/useSwrJobDomain';

/** UseJobDomain Props */
interface UseJobDomainProps {
  locale: Locale,
  setFormValues: SetFormValues,
  reqMirrorData?: Pick<MirrorData, 'title' | 'description'>,
  setMirrorData?: SetMirrorData,
  apiOccupation?: string,
  apiDomain?: string,
  apiDomainId?: string,
  shouldFetchDomain?: boolean,
}

/**
 * @description Hook for the job domain.
 * - `locale`: The current app locale.
 * - `setFormValues`: The form values setter.
 * - `reqMirrorData`: The required form mirror data.
 * - `setMirrorData`: The form mirror data setter.
 * - `apiOccupation`: The API occupation name.
 * - `apiDomain`: The API domain name.
 * - `apiDomainId`: The API domain ID.
 * - `shouldFetchDomain`: Whether to fetch the domain.
 */
export const useJobDomain = (props: UseJobDomainProps) => {
  // Destructure props
  const {
    locale,
    setFormValues,
    reqMirrorData,
    setMirrorData,
    apiOccupation,
    apiDomain,
    apiDomainId,
    shouldFetchDomain,
  } = props;

  // States
  // *************************
  // Occupation and domain name
  const [occupation, setOccupation] = useState<string | undefined>(apiOccupation);
  const [domain, setDomain] = useState<string | undefined>(apiDomain);

  // Show/hide domain suggest component (user chooses domain)
  const [showDomainSuggest, setShowDomainSuggest] = useState<boolean>(false);

  // User chosen occupation and domain
  const [autocompleteOccupation, setAutocompleteOccupation] = useState<string | undefined>(undefined);
  const [autocompleteDomain, setAutocompleteParentDomain] = useState<string | undefined>(undefined);

  // Keep track if the user has selected a domain
  const [hasSelectedDomain, setHasSelectedDomain] = useState<boolean>(false);
  const [currentDomainId, setCurrentDomainId] = useState<string | undefined>(apiDomainId);


  // Occupation (user selected has priority over API)
  // *************************
  const occupationName = useMemo(
    () => autocompleteOccupation || occupation,
    [autocompleteOccupation, occupation],
  );

  // Domain (user selected has priority over API)
  // ************************
  const domainName = useMemo(
    () => autocompleteDomain || domain,
    [autocompleteDomain, domain],
  );


  // SWR job domain
  // *************************
  const {
    data: jobDomainData,
    isValidating: isJobDomainLoading,
  } = useSwrJobDomain({
    locale,
    payload: {
      title: reqMirrorData?.title,
      ...(reqMirrorData?.description && { description: reqMirrorData?.description }),
    },
    shouldFetch: shouldFetchDomain && !hasSelectedDomain,
    errorCallBack: () => {
      // Reset the occupation and domain name
      setOccupation(undefined);
      setDomain(undefined);
    },
    onDataChanged: (currentData) => {
      // Set the domain input value if the data is available
      if (currentData?.escoId) {
        // Take care of bad setState error. Help React queue the state update.
        setTimeout(() => setFormValues({ domain: currentData.escoId }), 50);
        setCurrentDomainId(currentData.escoId);
        setMirrorData?.({ domain: currentData.escoId });
      }
      // Set the occupation and domain name
      setOccupation(currentData?.escoOccupationName || apiOccupation);
      setDomain(currentData?.domainName || apiDomain);
    },
  });

  // Set the domain id
  // *************************
  useEffect(() => {
    if (currentDomainId) {
      setTimeout(() => setFormValues({ domain: currentDomainId }), 50);
    }
  }, [currentDomainId, setFormValues]);


  // Return
  // *************************
  return {
    occupationName,
    domainName,
    jobDomainData,
    isJobDomainLoading,
    showDomainSuggest,
    setShowDomainSuggest,
    setAutocompleteOccupation,
    setAutocompleteParentDomain,
    setHasSelectedDomain,
  };
};
